import React from 'react';
import { Link } from 'react-router-dom';
import {
  Scissors,
  ShieldCheck,
  Award,
  Sparkles,
  ArrowRight,
  Store,
  Calendar,
  Heart,
} from 'lucide-react';
import styles from './about.module.css';

const AboutUsPage = () => {
  return (
    <div className={styles.page}>
      {/* Hero Banner */}
      <div className={styles.heroBanner}>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <Sparkles size={13} />
            <span>Our Mission &amp; Story</span>
          </div>

          <h1 className={styles.heroTitle}>
            Redefining Salon &amp; Barbershop Experiences Across India
          </h1>

          <p className={styles.heroSubtitle}>
            B4BARBER bridges premium salon craftsmanship with frictionless digital booking. No more
            telephone hold, waiting in salon queues, or surprise chair charges.
          </p>
        </div>
      </div>

      {/* Stats Counter Strip */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>500+</div>
          <div className={styles.statLabel}>Partner Salons &amp; Barbers</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>50,000+</div>
          <div className={styles.statLabel}>Completed Appointments</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>4.8 ★</div>
          <div className={styles.statLabel}>Average Customer Rating</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>100%</div>
          <div className={styles.statLabel}>Verified Hygiene &amp; Pros</div>
        </div>
      </div>

      {/* Story & Philosophy Section */}
      <div className={styles.storySection}>
        <div className={styles.storyCard}>
          <span className={styles.sectionTag}>The B4BARBER Journey</span>
          <h2 className={styles.storyTitle}>From Waiting in Queues to Instant Confirmation</h2>
          <p className={styles.storyParagraph}>
            B4BARBER was founded by <strong>Adarsh Raj</strong> and <strong>Abhimanyu Yadav</strong>{' '}
            with a clear ambition: getting a haircut, styling, or wellness treatment should be as
            effortless and dependable as ordering transport or dinner on your phone.
          </p>
          <p className={styles.storyParagraph}>
            Historically, people called busy receptionists, hoped for walk-in slots, and settled for
            unpredictable chair charges. We partner directly with verified salons, providing them
            with digital management software while giving consumers verified reviews, transparent
            price tags, and instant slot reservations.
          </p>
          <p className={styles.storyParagraph}>
            Customers get a serene, dignified booking flow. Barbershop owners get predictable
            revenue and full chair schedules.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className={styles.pillarCard}>
            <div className={styles.pillarIconBox}>
              <Scissors size={24} />
            </div>
            <h3 className={styles.pillarTitle}>Craftsmanship First</h3>
            <p className={styles.pillarText}>
              We champion the talent of master barbers and certified hair stylists, giving them
              tools to showcase their artistry.
            </p>
          </div>

          <div className={styles.pillarCard}>
            <div className={styles.pillarIconBox}>
              <ShieldCheck size={24} />
            </div>
            <h3 className={styles.pillarTitle}>Verified Standards</h3>
            <p className={styles.pillarText}>
              Every partner shop is audited for sanitization, quality tools, and transparent service
              menus before listing.
            </p>
          </div>
        </div>
      </div>

      {/* Core Values Section */}
      <div style={{ marginBottom: '4rem' }}>
        <h2
          style={{
            fontSize: '1.65rem',
            fontWeight: 800,
            color: '#0f172a',
            textAlign: 'center',
            margin: '0 0 2rem 0',
          }}
        >
          Why Customers &amp; Salons Choose B4BARBER
        </h2>

        <div className={styles.pillarsGrid}>
          <div className={styles.pillarCard}>
            <div className={styles.pillarIconBox}>
              <Calendar size={22} />
            </div>
            <h3 className={styles.pillarTitle}>Real-Time Slots</h3>
            <p className={styles.pillarText}>
              Book accurate 30-minute to 2-hour appointment slots with real-time seat availability.
            </p>
          </div>

          <div className={styles.pillarCard}>
            <div className={styles.pillarIconBox}>
              <Award size={22} />
            </div>
            <h3 className={styles.pillarTitle}>Fixed Price Menus</h3>
            <p className={styles.pillarText}>
              Know exactly what you will pay before you sit in the chair. No hidden surprises or
              upsells.
            </p>
          </div>

          <div className={styles.pillarCard}>
            <div className={styles.pillarIconBox}>
              <Heart size={22} />
            </div>
            <h3 className={styles.pillarTitle}>Dedicated Care</h3>
            <p className={styles.pillarText}>
              Friendly customer support and flexible rescheduling if your plans change.
            </p>
          </div>

          <div className={styles.pillarCard}>
            <div className={styles.pillarIconBox}>
              <Store size={22} />
            </div>
            <h3 className={styles.pillarTitle}>Empowering Locals</h3>
            <p className={styles.pillarText}>
              Enabling neighborhood barbershops and luxury salons to digitize and grow their
              clientele.
            </p>
          </div>
        </div>
      </div>

      {/* Founders Section */}
      <div className={styles.foundersSection}>
        <div className={styles.foundersHeader}>
          <h2 className={styles.foundersTitle}>Meet the Founders</h2>
          <p className={styles.foundersSubtitle}>
            Building India's most loved grooming platform from Noida
          </p>
        </div>

        <div className={styles.foundersGrid}>
          <div className={styles.founderCard}>
            <img
              src="https://ui-avatars.com/api/?name=Adarsh+Raj&background=dc2626&color=fff&size=200"
              alt="Adarsh Raj"
              className={styles.founderAvatar}
            />
            <h3 className={styles.founderName}>Adarsh Raj</h3>
            <div className={styles.founderRole}>Founder</div>
            <div className={styles.founderLinks}>
              <a
                href="https://www.linkedin.com/in/adarsh-raj17/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.founderLink}
              >
                LinkedIn Profile
              </a>
            </div>
          </div>

          <div className={styles.founderCard}>
            <img
              src="https://ui-avatars.com/api/?name=Abhimanyu+Yadav&background=dc2626&color=fff&size=200"
              alt="Abhimanyu Yadav"
              className={styles.founderAvatar}
            />
            <h3 className={styles.founderName}>Abhimanyu Yadav</h3>
            <div className={styles.founderRole}>Founder</div>
            <div className={styles.founderLinks}>
              <a
                href="https://www.linkedin.com/in/abhimanyu-yadav-362000/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.founderLink}
              >
                LinkedIn Profile
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Partner Invitation Banner */}
      <div className={styles.ctaBanner}>
        <div>
          <h3
            style={{
              fontSize: '1.45rem',
              fontWeight: 800,
              color: '#ffffff',
              margin: '0 0 0.35rem 0',
            }}
          >
            Ready for your best haircut yet?
          </h3>
          <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.9rem' }}>
            Discover top verified barbershops and beauty salons near your neighborhood.
          </p>
        </div>
        <Link to="/salons" className={styles.btnPrimary}>
          <span>Explore Salons</span>
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

export default AboutUsPage;
